import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import { formatTime } from "/imports/ui/views/TrainerDashboardView/trainerScheduleHelpers";
import { accentFor } from "/imports/ui/views/TrainerDashboardView/attendanceHelpers";
import { HOUR_REM, MIN_BLOCK_REM, rem } from "/imports/ui/components/scheduleGrid/gridHelpers";

// A block only reveals a detail line once it is tall enough to hold it. Thresholds
// are in rem to match the block height, which now scales with the root font size.
const SHOW_SERVICE_REM = 1.875;
const SHOW_PLACE_REM = 3.5;
const SHOW_CLIENTS_REM = 4.625;

function WeekBookingBlock({ item, gridStartMin, laneCount, onMark, onNote }) {
  // Attendance is the primary action, but a booking with nobody on it still
  // needs a way through to its note rather than being inert.
  const handleClick = onMark && item.booking.clientCount > 0 ? () => onMark(item.booking) : onNote ? () => onNote(item.booking) : undefined;
  const clickable = Boolean(handleClick);
  const topRem = ((item.start - gridStartMin) / 60) * HOUR_REM;
  const heightRem = Math.max(((item.end - item.start) / 60) * HOUR_REM, MIN_BLOCK_REM);
  const widthPct = 100 / laneCount;
  const timeLabel = [formatTime(item.start), formatTime(item.end)].filter(Boolean).join(" – ");
  const place = [item.booking.facilityName, item.booking.locationName].filter(Boolean).join(" · ");
  const clientLabel = `${item.booking.clientCount} ${item.booking.clientCount === 1 ? "client" : "clients"}`;
  const tooltip = [item.booking.serviceName, place, timeLabel, clientLabel].filter(Boolean).join("\n");
  const accent = accentFor(item.booking.clients);

  return (
    <Tooltip title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltip}</Box>} arrow enterTouchDelay={0}>
      <Box
        onClick={handleClick}
        sx={{
          position: "absolute",
          top: rem(topRem),
          height: rem(heightRem),
          left: `calc(${item.lane * widthPct}% + 2px)`,
          width: `calc(${widthPct}% - 4px)`,
          bgcolor: (theme) => alpha(accent || theme.palette.primary.main, 0.14),
          color: "text.primary",
          borderLeft: 3,
          borderColor: accent || "primary.main",
          borderRadius: 0.75,
          px: 0.75,
          py: 0.25,
          overflow: "hidden",
          fontSize: "0.6875rem",
          lineHeight: 1.25,
          cursor: clickable ? "pointer" : "default",
          transition: "background-color 120ms",
          "&:hover": clickable ? { bgcolor: (theme) => alpha(accent || theme.palette.primary.main, 0.24) } : undefined
        }}
      >
        <Typography noWrap sx={{ fontSize: "0.6875rem", fontWeight: 700, color: accent || "primary.dark" }}>
          {timeLabel}
        </Typography>
        {heightRem >= SHOW_SERVICE_REM && (
          <Typography noWrap sx={{ fontSize: "0.6875rem", fontWeight: 500 }}>
            {item.booking.serviceName || item.booking.title || "Booking"}
          </Typography>
        )}
        {heightRem >= SHOW_PLACE_REM && place && (
          <Typography noWrap sx={{ fontSize: "0.625rem", color: "text.secondary" }}>
            {place}
          </Typography>
        )}
        {heightRem >= SHOW_CLIENTS_REM && (
          <Typography noWrap sx={{ fontSize: "0.625rem", color: "text.secondary" }}>
            {clientLabel}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

export default WeekBookingBlock;
